import { BaSeTheme } from '../../../theme';
import { Color } from '../../../utils/color-transformation/color';
import { BaSeSmall2 } from '../../typography/small/small2';
import { BaSeText } from '../../typography/text/text';
import type { ButtonProps } from '../button-props';
import {
  iconButtonBorderRadius,
  iconButtonIconMapSize,
  iconButtonMapStyle,
  mapCustomColorsIconButtonValues,
} from '../map-button-style';

export type IconButtonSize =
  | 'mega-small'
  | 'extra-small'
  | 'small'
  | 'medium'
  | 'big';

type IconButtonColor = ButtonProps['color'];
type IconButtonShape = NonNullable<ButtonProps['shape']>;
type IconButtonSizeIcon = NonNullable<ButtonProps['sizeIcon']>;
type IconButtonType = NonNullable<ButtonProps['type']>;

interface IconButtonStyleParams {
  color: IconButtonColor;
  type: IconButtonType;
  isDisabled: boolean;
}

const extraSmallIconSize = 12;

const widthMap: Record<IconButtonSize, number> = {
  'mega-small': 1,
  'extra-small': 1.25,
  small: 2,
  medium: 2.5,
  big: 3,
};

const paragraphSize: Partial<Record<IconButtonSize, boolean>> = {
  big: true,
  medium: true,
};

const iconButtonPaddingMap: Record<
  IconButtonSize,
  (iconSize: number) => number
> = {
  big: (iconSize) => (40 - iconSize) / 2 + 4,
  medium: (iconSize) => (40 - iconSize) / 2,
  small: () => 4,
  'extra-small': () => 4,
  'mega-small': () => 1.5,
};

export const getIconButtonBorderRadius = (shape: IconButtonShape) =>
  iconButtonBorderRadius[shape] ?? iconButtonBorderRadius.circle;

export const getIconButtonIconSize = (
  size: IconButtonSize,
  sizeIcon: IconButtonSizeIcon,
) =>
  size === 'extra-small' || size === 'mega-small'
    ? extraSmallIconSize
    : (iconButtonIconMapSize[sizeIcon] ?? iconButtonIconMapSize.medium)
        .iconSize;

export const getIconButtonPadding = (size: IconButtonSize, iconSize: number) =>
  iconButtonPaddingMap[size](iconSize);

export const getIconButtonWidth = (size?: IconButtonSize) => {
  const width = widthMap?.[(size ?? 'medium') as IconButtonSize] ?? 0;

  if (width <= 0) {
    throw Error(
      `[BaSeIconButton] error: Informe o "size" com uma das opções:
          - "mega-small"
          - "extra-small"
          - "small"
          - "medium"
          - "big"`,
    );
  }

  return width;
};

export const getIconButtonBoxShadowFocus = (
  size: IconButtonSize,
  boxShadowFocus: string,
) =>
  size.includes('small') ? boxShadowFocus.replace('3', '2') : boxShadowFocus;

export const getIconButtonMessageComponent = (size: IconButtonSize) =>
  paragraphSize[size] ? BaSeText : BaSeSmall2;

export const getIconButtonStyle = ({
  color,
  type,
  isDisabled,
}: IconButtonStyleParams) => {
  const colorIsMapped =
    color === BaSeTheme.components.button.colors.default ||
    color === BaSeTheme.components.button.colors.negative ||
    color === BaSeTheme.components.button.colors.destructive ||
    color === BaSeTheme.components.button.colors.confirmation;

  const colorIsNegative = color === BaSeTheme.components.button.colors.negative;

  const getColorsAtributes = () => {
    if (color) {
      if (!colorIsMapped) {
        if (Color.validateColor(color as string)) {
          return mapCustomColorsIconButtonValues(color as string)?.[type];
        }
        return iconButtonMapStyle('default')[type]?.standard;
      }
      return iconButtonMapStyle(color as string)[type]?.standard;
    }
    return iconButtonMapStyle('default')[type]?.standard;
  };

  return colorIsNegative
    ? isDisabled
      ? iconButtonMapStyle('default')[type]?.negative.disabled
      : iconButtonMapStyle('default')[type]?.negative
    : isDisabled
      ? iconButtonMapStyle('default')[type]?.standard.disabled
      : getColorsAtributes();
};
